// Copy for /jumpstart-mw-ca (also served at /jumpstart-mw-ca-trial), verbatim from Martin's HTML prototype
// (jumpstart-mw-ca.html, 3 November 2026): the customer acquisition page sold as the 7-day free trial, rebuilt
// round one argument as the raising investment page was on 2 November. What every trial page says is in trial.js.
// The 19 October page and the 26 October trial page it replaces are in git before this commit
// (customerAcquisition.js, customerAcquisitionTrial.js).
import { ASSET_PATH } from '../../content'
import * as shared from './shared'
import * as trialCopy from './trial'

const META = {
  title: 'Jumpstart: Customer Acquisition | Find out what’s stopping people buying',
  description:
    'Find out what’s stopping people buying, and fix it in 30 days with Martin Warner. 7 days free, then $34.95.',
}

// the prototype's buy button and the lines round it; the card's cancel line names the account page only
const trial = {
  ...trialCopy.trial,
  ctaSub: '7 days free, then {price}',
  note: '**7 days free.** Billed **{price}** on day 8. Cancel anytime during the free trial and pay nothing.',
  sticky: { line: '**7 days free**, then {price}', cta: 'Start free trial' },
  card: {
    ...trialCopy.trial.card,
    title: '30-day Jumpstart · Customer Acquisition',
    flow: [
      ['Today', '{zero}'],
      ['Days 1–7', 'Free'],
      ['Day 8', '{price}'],
    ],
    terms:
      '**Trial terms.** A card is required to start. You are not charged for 7 days; on day 8 it is charged {price} once. Cancel anytime during the free trial from your account page to avoid the charge.',
  },
}

const hero = {
  ...trialCopy.hero,
  eyebrow: 'Jumpstart · Customer Acquisition',
  headline: 'Struggling to get customers?',
  headlineAccent: 'Find out what’s stopping people buying, and fix it in 30 days.',
  // the accent on its own line, as the prototype breaks it
  accentBlock: true,
  lede: 'Customers rarely tell you why they didn’t buy. Martin will show you, then help you fix it, whether you have no customers yet or your sales have stalled.',
  // bold runs are marked **like this**
  checks: [
    '**Find out in about 20 minutes** what’s stopping people buying, and what to fix first',
    '**Fix it with Martin:** your audience, your offer, your price and the right channel to test',
    '**Ask Martin anything about getting customers, any time,** plus live Q&As and weekly deep dives',
    '**Finish day 30 with a clear plan**, and Martin’s written review of what to do next',
  ],
  byline: {
    photo: { src: `${ASSET_PATH}/martin-warner-dark.jpg`, alt: '' },
    text: '**With Martin Warner:** founder and investor, $50MM exit, USA Today best-selling author',
  },
}

const video = {
  ...shared.video,
  eyebrow: null,
  headline: 'Hear from Martin:',
  headlineAccent: 'why customers aren’t buying, and how to fix it',
  label: 'Watch: why customers aren’t buying, and how to fix it',
  playLabel: 'Play the video: Hear from Martin why customers aren’t buying, and how to fix it',
  // the customer acquisition cuts, one per market since the price is spoken
  wids: { GB: { landscape: 'tz76d0z64g' }, US: { landscape: 'skw6lhae7b' } },
}

// Jack Valentine's result, on the card under the press strip: the figure stands where a name would
const proof = {
  big: null,
  text: 'Jack Valentine grew his sales ten-fold',
  name: '10× sales growth',
  sub: 'Real results from founders Martin has mentored.',
}

// "Sound familiar?" as six pains and the reframe under them
const problem = {
  eyebrow: 'Sound familiar?',
  headline: 'Getting customers is hard',
  headlineAccent: 'when you can’t see what’s wrong.',
  pains: [
    'You have no customers yet, or nowhere near enough',
    'People say they like it, but few actually buy',
    'You’re posting, advertising and networking, and nothing sticks',
    'You’re not sure who your best customers really are',
    'You don’t know what to charge, so you guess or go cheap',
    'Meanwhile, time and savings are running down',
  ],
  reframe: {
    text: '**It’s rarely your product.** Winning customers comes down to the same handful of things, and when one is off, sales stall without telling you why. Jumpstart shows you which one, and Martin helps you fix it.',
    link: 'Find your #1 blocker in one tap ↓',
  },
}

// "Find your #1 blocker": one tap; the tap's key is the visitor's level for Meta
const blocker = {
  eyebrow: 'Find your #1 blocker',
  headline: 'What’s holding',
  headlineAccent: 'your sales back?',
  lede: 'Tap the one that sounds most like you, and see what to do about it.',
  tapNote: 'Tap an answer to see your #1 blocker and what to do first.',
  kicker: 'Your #1 blocker',
  // round the blocker's `fix`
  fix: ['Jumpstart works on ', ' with you from day 1, with Martin’s feedback.'],
  options: [
    {
      key: 'idea',
      label: 'I’m not sure who my best customers are',
      name: 'Knowing who it’s for',
      fix: 'knowing who your customers are',
      intro:
        'You are not yet sure exactly who your best customers are, and that makes every other part of getting customers harder.',
      bullets: [
        'Talk to five people who fit your best-customer description before you spend on anything else',
        'Write down who they are, what they are trying to get done, and what it costs them to leave it unsolved',
        'Narrow to one group you can reach this month, not everyone who might one day buy',
      ],
    },
    {
      key: 'product',
      label: 'People like it, but don’t see a reason to buy now',
      name: 'Your offer',
      fix: 'your offer',
      intro:
        'People may like what you do without seeing a clear reason to buy it now. An unclear offer quietly costs you customers.',
      bullets: [
        'Describe your offer in one sentence a customer would repeat to a friend',
        'Name the result they get, not just the features you provide',
        'Test the offer with the five people you talk to and note where they hesitate',
      ],
    },
    {
      key: 'customers',
      label: 'I get interest, but few people buy',
      name: 'Winning customers',
      fix: 'turning interest into sales',
      intro:
        'People show interest, but not enough of them become buyers. The gap is usually in the steps between interest and a decision.',
      bullets: [
        'List every step from first contact to payment and mark where people drop out',
        'Ask three people who did not buy what stopped them',
        'Fix the single biggest drop-out point before you add more leads',
      ],
    },
    {
      key: 'marketing',
      label: 'I don’t know where to find customers',
      name: 'Reaching customers',
      fix: 'reaching customers',
      intro: 'Your customers exist, but you have not found a reliable way to put your offer in front of them.',
      bullets: [
        'List the places your best customers already spend their time and attention',
        'Pick one channel and run it properly for 30 days instead of spreading across five',
        'Track what each channel costs you in time and money per customer won',
      ],
    },
    {
      key: 'proof',
      label: 'I don’t have reviews or results to show yet',
      name: 'Proof & reviews',
      fix: 'your proof',
      intro: 'Buyers want evidence before they trust you, and right now there is not enough of it to go around.',
      bullets: [
        'Collect the results your current customers have had, in their own words',
        'Turn one customer story into a short written case study',
        'Put proof next to every place you ask someone to buy',
      ],
    },
    {
      key: 'team',
      label: 'I’m doing everything myself',
      name: 'Who helps you',
      fix: 'freeing up your time',
      intro: 'You are doing most of the work of winning customers on your own, and that caps how fast you can grow.',
      bullets: [
        'Write down each step of how customers find you and buy from you',
        'Pick the step that takes most of your time and document how it is done',
        'Name one person, or one tool, to help with that step next',
      ],
    },
    {
      key: 'planning',
      label: 'I don’t have a plan for growing',
      name: 'Your growth plan',
      fix: 'your growth plan',
      intro:
        'You do not yet have a clear plan for how many customers you want, from where, and by when. Without one, it is hard to know what is working.',
      bullets: [
        'Set one customer target for the next 90 days',
        'Work back from that target to the leads, conversations and sales it needs',
        'Review your numbers every week and change one thing at a time',
      ],
    },
  ],
}

const journey = {
  eyebrow: 'Your 30 days',
  headline: 'How Jumpstart shows you',
  headlineAccent: 'where your next customers will come from',
  lede: null,
  columns: 2,
  steps: [
    {
      when: 'Day 1 · Assess',
      title: 'Customer Acquisition Assessment',
      text: 'Together with your Initial Assessment, about 20 minutes. Martin reviews both himself and names your #1 blocker.',
    },
    {
      when: 'Weeks 1–3 · Fix',
      title: 'Deep dives and customer templates',
      text: 'A deep dive presentation each week, plus three 20-minute templates done on your own business: customer graph, competitor pricing and metrics, and channel build.',
    },
    {
      when: 'All month · Ask',
      title: 'Martin, whenever you’re stuck',
      text: '24/7 mentoring with replies typically within 24 to 48 hours, 2 live Q&As where you can bring your offer, your marketing or your numbers, and a short daily insight.',
    },
    {
      when: 'Day 30 · Review',
      title: 'Full Customer Acquisition Review',
      text: 'Retake the full assessment, with Martin’s written review: what’s working, what to fix next and where your next customers will come from.',
    },
  ],
  deepDives: {
    title: 'Your 4 deep dives',
    items: [
      'Customer growth: who really buys, and why',
      'Your offer and price: making it easy to say yes',
      'Finding your channel: where your customers already are',
      'Turning interest into sales, and sales into a system',
    ],
  },
  finish: {
    title: 'By day 30 you’ll have',
    items: [
      'One clear audience you can find and reach',
      'Your price and position against competitors',
      'One channel under test, with a keep-or-stop rule',
      'Martin’s written review and your next step',
    ],
  },
  beyond: {
    kicker: 'Beyond customers',
    title: 'Customers are the heartbeat of a business, so Jumpstart covers the rest of yours.',
    text: 'Your mentoring, live Q&As and daily insights aren’t limited to marketing. Bring questions on pricing, your team, raising money or anything else, and your Day 30 review includes your next step for the whole business.',
  },
  note: 'No customers yet? Every template has a path for you: five conversations instead of past customers.',
}

// the gradient panel under "Your 30 days"
const invite = {
  kicker: 'Your 30-day journey',
  headline: 'Join an exhilarating 30-day journey to find your next customers, and much more.',
  must: 'Don’t spend another dollar on marketing until you’ve done it.',
  text: 'Over 30 days, Martin shows you exactly why people aren’t buying, helps you fix what’s holding your sales back, and teaches you far more about building a business along the way. You finish with clear next steps to grow as an entrepreneur, and as a business.',
  items: [
    'See your business through your customers’ eyes',
    'Fix your #1 blocker, with Martin alongside you',
    'Finish with next steps for you and your business',
  ],
  cta: 'Start your 30-day journey',
  fine: 'A month packed with value, and it starts free.',
}

const mentor = {
  ...trialCopy.mentor,
  stats: [
    { figure: '$50MM', text: 'UK tech record exit, 17 months after co-founding botObjects' },
    { figure: '120+', text: 'Patents' },
    { figure: '12', text: 'Products built and taken to market' },
    { figure: '24', text: 'Years teaching entrepreneurship' },
  ],
  credentials: [
    ['USA Today best-selling author of ', 'The Startup Story: An Entrepreneur’s Journey from Idea to Exit'],
    ['First British entrepreneur to build an electric aviation aircraft'],
    ['No. 1 in T3 Magazine’s Top 100 products for his ProDesk3D printer'],
  ],
  interviews: null,
}

// four growth stories and the member videos, under the mentor's bio rather than in a section of their own
const testimonials = {
  ...shared.testimonials,
  title: 'What founders say about growing with Martin',
  quotes: [
    {
      tag: 'Business expanded',
      quote: '“Applying Martin’s strategies, I successfully expanded my business nationwide.”',
      name: 'Richard Cardigan',
    },
    {
      tag: 'Sales and marketing',
      quote:
        '“Martin showed me how to simplify my early sales process, and prioritise sales and marketing spend, and my testing took off!”',
      name: 'Andy Brightman',
    },
    {
      tag: 'Pricing',
      quote:
        '“Jumpstart helped me fix my pricing that was limiting my customer growth, I now have better margins and 3x more sales.”',
      name: 'Michael Richter',
    },
    {
      tag: 'Business plan',
      quote:
        '“This program is a one of a kind built on really understanding the entrepreneur, my business plan needed re-examination – and now I am selling!”',
      name: 'Christine Malvern',
    },
  ],
  videosTitle: trialCopy.memberVideos.title,
  videos: trialCopy.memberVideos.slugs,
  disclaimer: ['Individual results vary. See our ', 'Earnings and Income Disclaimer', '.'],
}

// the price card alone: the prototype has no list of what is included above it
const offer = {
  eyebrow: 'Start today',
  headline: 'Start free today.',
  headlineAccent: '{price} only if you stay.',
  lede: 'Every month spent chasing the wrong customers, or the wrong channel, costs far more than {price}.',
  stack: null,
  card: { label: 'Jumpstart offer' },
}

const compare = {
  // after the price card and before the founders' questions
  beforeFaq: true,
  eyebrow: 'What’s different?',
  headline: 'Why not keep trying things,',
  headlineAccent: 'or take a course?',
  lede: 'Personal feedback from a founder who has built, sold and backed businesses, for {price} rather than $150–$600 an hour for a coach.',
  // each option's rows, marked yes, no or mixed
  options: [
    {
      title: 'Jumpstart',
      me: true,
      rows: [
        { label: 'Cost', text: '{price} once', mark: 'yes' },
        { label: 'Start', text: 'Today', mark: 'yes' },
        { label: 'Fit', text: 'Built around your business', mark: 'yes' },
        { label: 'Feedback', text: 'From Martin himself', mark: 'yes' },
      ],
    },
    {
      title: 'Going it alone',
      rows: [
        { label: 'Cost', text: 'Free, but costs months', mark: 'mixed' },
        { label: 'Start', text: 'Today', mark: 'yes' },
        { label: 'Fit', text: 'Trial and error', mark: 'no' },
        { label: 'Feedback', text: 'None', mark: 'no' },
      ],
    },
    {
      title: 'Online courses',
      rows: [
        { label: 'Cost', text: 'Monthly fee or one-off', mark: 'mixed' },
        { label: 'Start', text: 'Today', mark: 'yes' },
        { label: 'Fit', text: 'Same for everyone', mark: 'no' },
        { label: 'Feedback', text: 'Rarely', mark: 'no' },
      ],
    },
    {
      title: '1:1 coaching',
      rows: [
        { label: 'Cost', text: '$150–$600 an hour', mark: 'no' },
        { label: 'Start', text: 'Varies', mark: 'mixed' },
        { label: 'Fit', text: 'Built around you', mark: 'yes' },
        { label: 'Feedback', text: 'Depends on the coach', mark: 'mixed' },
      ],
    },
  ],
  marks: { yes: 'Yes', no: 'No', mixed: 'Partly' },
  note: 'General comparison. Coaching prices are typical published rates; costs vary.',
}

const faq = {
  ...shared.faq,
  headline: 'Questions',
  headlineAccent: 'founders ask',
  items: [
    {
      q: 'How does the free trial work?',
      a: 'Start today for {zero} and try Jumpstart for 7 days. On day 8 your card is billed {price}, once. Cancel anytime during the free trial from your account page and you pay nothing.',
    },
    {
      q: 'I don’t have any customers yet. Is this for me?',
      a: 'Yes. Every template has a path for you: five conversations instead of past customers. You start by finding out who really needs what you do.',
    },
    {
      q: 'I already have some customers. Is it too basic?',
      a: 'No. You will find out exactly where your next customers will come from, and fix the step that is losing you the ones you already attract.',
    },
    {
      q: 'Does Jumpstart run my marketing or ads for me?',
      a: 'No. Jumpstart isn’t an agency. Martin shows you who to target, what to offer and which channel to test, so you leave able to do it yourself.',
    },
    {
      q: 'What are the three templates?',
      a: 'Customer graph (map who really buys and where they come from), Competitor pricing and metrics (compare what others charge, then check four plain numbers), and Channel build (pick one channel and run a four-week test with a keep-or-stop rule).',
    },
    {
      q: 'Is it only about getting customers?',
      a: 'Customers are the focus, but they are the heartbeat of the whole business. Your mentoring, live Q&As and daily insights cover the rest of your business too, and your Day 30 review includes your next step for the business.',
    },
    {
      q: 'How much time does it take?',
      a: 'Allow 2 to 3 hours a week. Your first two assessments take about 20 minutes together, and each template takes about 20 minutes.',
    },
    {
      q: 'Do I really get Martin?',
      a: 'Yes. Martin reviews your work himself, answers your questions in the live Q&As and replies in the 24/7 mentoring portal, typically within 24 to 48 hours.',
    },
    {
      q: 'Does Jumpstart guarantee more customers?',
      a: 'No, and nobody honestly can. Jumpstart gives you a diagnosis, a plan and Martin’s feedback; results depend on what you do with them. See our Earnings and Income Disclaimer.',
    },
  ],
}

const finalCta = {
  ...trialCopy.finalCta,
  headline: 'Know where your next customers',
  headlineAccent: 'will come from.',
  lede: 'A month from now, you could still be guessing. Or you could have a clear audience, offer and channel, with Martin’s written review behind you.',
  // the billing line under the closing button, as under the hero's
  line: 'note',
}

const content = {
  key: 'ca',
  trial,
  // sections whose pieces are narrower than the column read centred from tablet width up (hero, proof, closing)
  centred: true,
  META,
  trusted: shared.trusted,
  hero,
  video,
  proof,
  // the member videos sit with the founders' quotes under the mentor's bio
  memberVideos: null,
  problem,
  blocker,
  // no sliders, no drop-out chart and no ladder: the blocker above stands for all three
  scorer: null,
  transformation: null,
  stage: null,
  journey,
  invite,
  advice: null,
  compare,
  mentor,
  testimonials,
  offer,
  faq,
  finalCta,
}

export default content
